import { mat4 } from 'gl-matrix';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomRegistration } from './dicomRegistration';
import { mat4ToRowMajorArray } from './utils';

export class DicomRigidRegistration extends DicomRegistration {
    public readonly transformation: mat4;

    constructor(
        sopUid: string,
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        contentLabel: string,
        transformation: mat4,
        dataset: DcmJsDatasetWrapper
    ) {
        super(
            sopUid,
            sourceFrameOfReferenceUid,
            targetFrameOfReferenceUid,
            contentLabel,
            dataset
        );
        this.transformation = transformation;
    }

    /**
     * Syncs the current transformation mat4 back into the underlying DICOM
     * dataset so that getAsDicomBlob() serializes the up-to-date matrix.
     *
     * Returns whether the dataset value actually changed (compare before
     * write), so callers can treat a no-op sync — e.g. an export of an
     * unchanged matrix — as a non-event. A representational mismatch
     * conservatively counts as changed: worst case an identical matrix is
     * re-committed, never a missed change.
     */
    public syncTransformationToDataset(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ds = this.datasetWrapper.dataset as any;
        if (!ds?.RegistrationSequence) {
            return false;
        }

        for (const entry of ds.RegistrationSequence) {
            if (entry.FrameOfReferenceUID === this.targetFrameOfReferenceUid) {
                const next = mat4ToRowMajorArray(this.transformation);
                const matrixItem =
                    entry.MatrixRegistrationSequence.MatrixSequence;
                const current = matrixItem.FrameOfReferenceTransformationMatrix;
                const unchanged =
                    Array.isArray(current) &&
                    current.length === next.length &&
                    current.every(
                        (value: unknown, i: number) => Number(value) === next[i]
                    );
                if (unchanged) {
                    return false;
                }
                matrixItem.FrameOfReferenceTransformationMatrix = next;
                return true;
            }
        }
        return false;
    }
}
